import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { AuthenticatedImage } from '../components/OptimizedImage';
import './AdminTable.css';

function AdminMessages() {
  const [view, setView] = useState('conversations'); // 'conversations', 'all', or 'conversation-detail'
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }

    if (view === 'conversations') {
      fetchConversations();
    } else if (view === 'all') {
      fetchMessages();
    } else if (view === 'conversation-detail' && selectedConversation) {
      fetchConversationMessages();
    }
  }, [user, navigate, view, currentPage, selectedConversation]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/messages/conversations');
      setConversations(response.data.conversations || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/messages', {
        params: { page: currentPage, per_page: 50 }
      });
      setMessages(response.data.messages || []);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.messages.failedToLoadMessages'));
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationMessages = async () => {
    if (!selectedConversation) return;

    try {
      setLoading(true);
      const { user1, user2 } = selectedConversation;
      const response = await api.get('/api/admin/messages/conversation', {
        params: { user1_id: user1.id, user2_id: user2.id }
      });
      setConversationMessages(response.data.messages || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.messages.failedToLoadConversation'));
    } finally {
      setLoading(false);
    }
  };

  const openConversation = (conversation) => {
    setSelectedConversation(conversation);
    setView('conversation-detail');
  };

  const backToConversations = () => {
    setSelectedConversation(null);
    setConversationMessages([]);
    setView('conversations');
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm(t('admin.messages.deleteConfirm'))) return;

    try {
      await api.delete(`/api/admin/messages/${messageId}`);
      toast.success(t('admin.messages.deleteSuccess'));
      if (view === 'all') {
        fetchMessages();
      } else {
        fetchConversations();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.messages.failedToDelete'));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.messages.title')}</h1>
          <p>{t('admin.messages.subtitle')}</p>
        </div>
      </div>

      <div className="filters">
        <div className="view-toggle">
          <button
            className={view === 'conversations' ? 'active' : ''}
            onClick={() => setView('conversations')}
          >
            {t('admin.messages.conversations')}
          </button>
          <button
            className={view === 'all' ? 'active' : ''}
            onClick={() => setView('all')}
          >
            {t('admin.messages.allMessages')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">{t('admin.messages.loading')}</div>
      ) : (
        <>
          {view === 'conversations' ? (
            <div className="conversations-list">
              {conversations.map((conv, index) => (
                <div
                  key={index}
                  className="conversation-card clickable"
                  onClick={() => openConversation(conv)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="conversation-users">
                    <div className="user-info">
                      <strong>{conv.user1.name}</strong>
                      <span className="email">{conv.user1.email}</span>
                    </div>
                    <div className="arrow">↔️</div>
                    <div className="user-info">
                      <strong>{conv.user2.name}</strong>
                      <span className="email">{conv.user2.email}</span>
                    </div>
                  </div>
                  {conv.last_message && (
                    <div className="last-message">
                      <div className="message-preview">
                        {conv.last_message.content
                          ? `${conv.last_message.content.substring(0, 100)}${conv.last_message.content.length > 100 ? '...' : ''}`
                          : conv.last_message.attachment_url
                            ? `📎 ${conv.last_message.attachment_type === 'image' ? t('admin.messages.image') : conv.last_message.attachment_name || t('admin.messages.fileAttachment')}`
                            : t('admin.messages.noContent')
                        }
                      </div>
                      <div className="message-meta">
                        {new Date(conv.last_message.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {conv.unread_count > 0 && (
                    <div className="unread-badge">{conv.unread_count} {t('admin.messages.unread')}</div>
                  )}
                </div>
              ))}
              {conversations.length === 0 && (
                <div className="no-results">{t('admin.messages.noConversations')}</div>
              )}
            </div>
          ) : view === 'conversation-detail' ? (
            <div className="conversation-detail">
              <div className="conversation-header">
                <button onClick={backToConversations} className="btn-back">
                  ← {t('admin.messages.backToConversations')}
                </button>
                {selectedConversation && (
                  <div className="conversation-title">
                    <h3>
                      {selectedConversation.user1.name} ↔ {selectedConversation.user2.name}
                    </h3>
                  </div>
                )}
              </div>
              <div className="messages-thread">
                {conversationMessages.map((msg) => (
                  <div key={msg.id} className={`message-bubble ${msg.sender?.id === selectedConversation?.user1.id ? 'left' : 'right'}`}>
                    <div className="message-sender">
                      <strong>{msg.sender.name}</strong>
                      <span className="message-time">
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="message-text">
                      {msg.content || (msg.attachment_type ? `[${msg.attachment_type}: ${msg.attachment_name || 'attachment'}]` : t('admin.messages.noContent'))}
                    </div>
                    {msg.attachment_url && (
                      <div className="message-attachment">
                        {msg.attachment_type === 'image' ? (
                          <AuthenticatedImage
                            src={msg.attachment_url}
                            alt="Attachment"
                            style={{ maxWidth: '300px', borderRadius: '8px', display: 'block', marginTop: '8px' }}
                          />
                        ) : (
                          <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                            📎 {msg.attachment_name || t('admin.messages.downloadAttachment')}
                          </a>
                        )}
                      </div>
                    )}
                    <div className="message-actions">
                      <button
                        onClick={() => deleteMessage(msg.id)}
                        className="btn-sm btn-danger"
                      >
                        {t('admin.messages.delete')}
                      </button>
                    </div>
                  </div>
                ))}
                {conversationMessages.length === 0 && (
                  <div className="no-results">{t('admin.messages.noMessages')}</div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('admin.messages.from')}</th>
                      <th>{t('admin.messages.to')}</th>
                      <th>{t('admin.messages.message')}</th>
                      <th>{t('admin.messages.date')}</th>
                      <th>{t('admin.messages.status')}</th>
                      <th>{t('admin.messages.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <tr key={msg.id}>
                        <td>
                          <div className="user-cell">
                            <div>{msg.sender.name}</div>
                            <div className="sub-text">{msg.sender.email}</div>
                          </div>
                        </td>
                        <td>
                          <div className="user-cell">
                            <div>{msg.receiver.name}</div>
                            <div className="sub-text">{msg.receiver.email}</div>
                          </div>
                        </td>
                        <td>
                          <div className="message-content">
                            {msg.content && (
                              <div>
                                {`${msg.content.substring(0, 80)}${msg.content.length > 80 ? '...' : ''}`}
                              </div>
                            )}
                            {msg.attachment_url && (
                              <div style={{ marginTop: msg.content ? '8px' : '0' }}>
                                {msg.attachment_type === 'image' ? (
                                  <AuthenticatedImage
                                    src={msg.attachment_url}
                                    alt="Attachment"
                                    style={{ maxWidth: '150px', maxHeight: '100px', borderRadius: '4px', display: 'block' }}
                                  />
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    📎 {msg.attachment_name || t('admin.messages.fileAttachment')}
                                  </div>
                                )}
                              </div>
                            )}
                            {!msg.content && !msg.attachment_url && (
                              <div style={{ color: '#999' }}>{t('admin.messages.noContent')}</div>
                            )}
                          </div>
                        </td>
                        <td>{new Date(msg.created_at).toLocaleDateString()}</td>
                        <td>
                          <span className={`status-badge ${msg.is_read ? 'success' : 'warning'}`}>
                            {msg.is_read ? t('admin.messages.read') : t('admin.messages.unreadStatus')}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => deleteMessage(msg.id)}
                            className="btn-sm btn-danger"
                          >
                            {t('admin.messages.delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {messages.length === 0 && (
                  <div className="no-results">{t('admin.messages.noMessages')}</div>
                )}
              </div>

              {pagination && pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    {t('common.previous')}
                  </button>
                  <span>{t('property.page')} {pagination.page} {t('property.of')} {pagination.pages}</span>
                  <button
                    disabled={currentPage === pagination.pages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default AdminMessages;
